import React, {useContext, createContext, useRef, useCallback} from 'react';

export interface NodeLocation {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface NodeLocationInfo {
    actual: NodeLocation;
    simulated: NodeLocation;
}

export interface NodeLocationBall {
    [id: string]: NodeLocationInfo;
}

export interface NodeCallbackState {
    nodeCallbacks: {[id: string]: () => NodeLocationInfo};
    addNodeCallback: (id: string, fn: () => NodeLocationInfo) => void;
    scaleRef: React.MutableRefObject<number>;
    getNodesCurrentLocations: () => NodeLocationBall;
}

const NodeCallbackContext = createContext<NodeCallbackState>({
    nodeCallbacks: {},
    addNodeCallback: () => {},
    getNodesCurrentLocations: () => {
        return {};
    },
    scaleRef: {current: 1},
});

export const useNodeCallbackContext = () => {
    return useContext(NodeCallbackContext);
};

export const NodeCallbackContextProvider: React.FC<React.PropsWithChildren> = ({children}) => {
    const nodeCallbacks = useRef<{[id: string]: () => NodeLocationInfo}>({});
    const scaleRef = useRef<number>(1);

    const addNodeCallback = useCallback((id: string, fn: () => NodeLocationInfo) => {
        nodeCallbacks.current[id] = fn;
    }, []);

    const getNodesCurrentLocations = useCallback(() => {
        const locations: NodeLocationBall = {};
        if (nodeCallbacks.current) {
            Object.keys(nodeCallbacks.current).forEach((id) => {
                locations[id] = nodeCallbacks.current[id]();
            });
        }

        return locations;
    }, [nodeCallbacks]);

    return (
        <NodeCallbackContext.Provider
            value={{getNodesCurrentLocations, nodeCallbacks: nodeCallbacks.current, addNodeCallback, scaleRef}}
        >
            {children}
        </NodeCallbackContext.Provider>
    );
};
